'use client';

import { useCallback, useState } from 'react';
import { Container } from '@/components/primitives/Container/Container';
import { toPascalCase } from '@/utils/string-utils';
import { IBaseComponentProps, WithMetadata } from '@/lib/types';
import { IStandardContentPageComponents } from '@/.generated';
import RichTextWrapper from '@/helpers/Wrappers/RichTextWrapper/RichTextWrapper';
import { Col, Row } from '@/components/primitives/Grid';
import { resolveColSpan } from '@/utils/grid-utils';
import { defaultVariants } from './FlipCards.styles';
import { FlipCard } from './FlipCard';

type FlipCardsProps = WithMetadata<IBaseComponentProps> &
  IStandardContentPageComponents['flip_cards'];

// Default FlipCards layout — renders a responsive grid; on mobile any number of cards
// can be flipped at once (desktop flips on hover)
const Default = (props: FlipCardsProps) => {
  const {
    componentName,
    _metadata,
    inline_navigation_id,
    hide_component,
    layout,
    card_foot_note,
    cards,
    full_width,
    $,
  } = props;
  const totalCards = cards?.length;
  const cardsPerRow = Number(layout) || 3;
  const { base, footNote } = defaultVariants();

  // Mobile tap-flip state (desktop flips on CSS :hover and ignores this). A Set,
  // not a single index, so multiple cards can stay flipped at once — no mutex.
  const [activeIndices, setActiveIndices] = useState<Set<number>>(() => new Set());

  // Toggle one card's flipped state, leaving any other flipped cards as they are.
  const handleToggle = useCallback((idx: number) => {
    setActiveIndices((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  if (hide_component) return null;

  if (!totalCards) return null;

  return (
    <Container
      componentName={componentName}
      metaId={_metadata?.uid}
      id={inline_navigation_id}
      containerBleed={full_width}
    >
      <div className={base()}>
        <Row>
          {cards.map((cardItem, index) => {
            const cardFrontSide = cardItem?.card_sides?.[0];
            const cardBackSide = cardItem?.card_sides?.[1];

            if (!cardFrontSide) return null;

            return (
              <Col key={index} ml={resolveColSpan(cardsPerRow, index, totalCards)}>
                <FlipCard
                  front={cardFrontSide}
                  back={cardBackSide}
                  index={index}
                  isActive={activeIndices.has(index)}
                  onToggle={handleToggle}
                />
              </Col>
            );
          })}
        </Row>
        {card_foot_note && (
          <RichTextWrapper
            content={card_foot_note}
            cslpAttribute={$?.card_foot_note}
            className={footNote()}
          />
        )}
      </div>
    </Container>
  );
};

const variants = {
  Default,
};

export const FlipCards = (props: FlipCardsProps) => {
  const Component = props.component_variant
    ? variants[toPascalCase(props.component_variant) as keyof typeof variants]
    : Default;
  return <Component {...props} />;
};
